/* eslint no-param-reassign: ["error", { "props": false }] */
import { omitBy, isNil, keyBy } from 'lodash';
import pathToRegexp from 'path-to-regexp';
import http from '@/http';

import { resourceReponseDeserializer, resourceErrorsReponseDeserializer, resourceSerializer, paginationSerializer, filterSerializer } from '@/utils/json-api';

const defaultState = () => ({
  request: null,
  pending: true,
  all: undefined,
  included: undefined,
  etag: undefined,
  error: undefined,
  meta: {
    page: undefined,
    per: undefined,
    count: undefined,
    pages: undefined,
  },
  filters: {
    deleted: false,
  },
  sort: undefined,
  pagination: {
    page: undefined,
    per: undefined,
    perOptions: undefined,
  },
});

export default ({
  namespace, url, pendingDelay = 500, server = {},
}) => {
  const RESOURCES = namespace.toUpperCase().split('/').join('_');
  const SET_RESOURCES_REQUEST = `SET_${RESOURCES}_REQUEST`;
  const SET_RESOURCES_PENDING = `SET_${RESOURCES}_PENDING`;
  const SET_RESOURCES_SUCCESS = `SET_${RESOURCES}_SUCCESS`;
  const SET_RESOURCES_FAILURE = `SET_${RESOURCES}_FAILURE`;
  const SET_RESOURCES_PAGINATION = `SET_${RESOURCES}_PAGINATION`;
  const SET_RESOURCES_SORT = `SET_${RESOURCES}_SORT`;
  const SET_RESOURCES_FILTERS = `SET_${RESOURCES}_FILTERS`;
  const toUrl = pathToRegexp.compile(url);

  const { attributes = [] } = server;
  const attributesByKey = keyBy(attributes, 'key');

  function filtersSerializer(filters) {
    return Object.assign(
      {},
      ...Object
        .entries(filters)
        .map(([key, value]) => {
          const { prop = key, ...other } = attributesByKey[key] || {};
          const {
            filterKey = prop,
            filterSerialize = filterSerializer,
          } = other;
          return filterSerialize(filterKey, value);
        }),
    );
  }

  function sortSerializer(sort) {
    if (!sort) return {};
    return {
      _sort: sort.map(([attr]) => {
        const { sortKey = attr } = attributesByKey[attr] || {};
        return sortKey;
      }).join(','),
      _order: sort.map(([, order]) => order).join(','),
    };
  }

  return {
    namespaced: true,
    state: defaultState,
    getters: {
    },
    mutations: {
      [SET_RESOURCES_REQUEST](state, request) {
        state.request = request;
        state.error = null;
      },
      [SET_RESOURCES_PENDING](state) {
        state.pending = true;
      },
      [SET_RESOURCES_SUCCESS](state, { data, headers }) {
        const { etag } = headers;

        if (!etag || state.etag !== etag) {
          state.etag = etag;
          const { data: all, included } = data;
          state.all = all;
          state.included = included;
        }

        Object.assign(state.meta, {
          page: parseInt(headers['x-current-page'], 10),
          per: parseInt(headers['x-per-page'], 10),
          count: parseInt(headers['x-total-count'], 10),
          pages: parseInt(headers['x-total-pages'], 10),
        });
        Object.assign(state.pagination, {
          page: parseInt(headers['x-current-page'], 10),
          per: parseInt(headers['x-per-page'], 10),
        });

        if (state.request) {
          clearTimeout(state.request.pending);
        }
        state.request = null;
        state.pending = false;
      },
      [SET_RESOURCES_FAILURE](state, error) {
        state.error = error;

        if (state.request) {
          clearTimeout(state.request.pending);
        }
        state.request = null;
        state.pending = false;
      },
      [SET_RESOURCES_PAGINATION](state, pagination) {
        state.pagination = {
          ...state.pagination,
          ...pagination,
        };
      },
      [SET_RESOURCES_SORT](state, sort) {
        state.pagination = {
          ...state.pagination,
          page: 1,
        }; // reset page
        state.sort = sort;
      },
      [SET_RESOURCES_FILTERS](state, filters) {
        state.pagination = {
          ...state.pagination,
          page: 1,
        }; // reset page
        state.filters = omitBy({
          ...state.filters,
          ...filters,
        }, isNil);
      },
    },
    actions: {
      async fetchResources({ commit, state }, {
        ...config
      } = {}) {
        try {
          const {
            pagination, sort, filters, request: previousRequest,
          } = state;
          const { deleted, ...columnFilters } = filters;

          // Avoid pending if really fast
          const pending = previousRequest ? previousRequest.pending : setTimeout(() => {
            commit(SET_RESOURCES_PENDING);
          }, pendingDelay);
          commit(SET_RESOURCES_REQUEST, { pending });

          const response = (await http.get(toUrl(config), {
            'axios-debounce': { id: `fetchResources_${namespace}` },
            params: {
              _deleted: deleted,
              ...paginationSerializer(pagination),
              ...sortSerializer(sort, attributes),
              ...filtersSerializer(columnFilters),
            },
          })) |> (_ => resourceReponseDeserializer(_, attributes));

          commit(SET_RESOURCES_SUCCESS, response);
          return response;
        } catch (error) {
          if (http.isCancel(error)) {
            // canceled by next request
            return undefined;
          }
          commit(SET_RESOURCES_FAILURE, error);
          throw error;
        }
      },
      async deleteResources(_, { ids }) {
        try {
          const response = await http.delete(`${url}`, { data: { ids } });
          return resourceReponseDeserializer(response, attributes);
        } catch (error) {
          throw resourceErrorsReponseDeserializer(error, attributes);
        }
      },
      async restoreResources(_, { ids }) {
        try {
          const response = await http.patch(`${url}/restore`, { ids });
          return resourceReponseDeserializer(response, attributes);
        } catch (error) {
          throw resourceErrorsReponseDeserializer(error, attributes);
        }
      },
      async createResource(_, { data }) {
        try {
          const serialized = resourceSerializer(data, attributes);
          const response = await http.post(url, serialized);
          return resourceReponseDeserializer(response, attributes);
        } catch (error) {
          throw resourceErrorsReponseDeserializer(error, attributes);
        }
      },
      async deleteResource(_, { id }) {
        try {
          const response = await http.delete(`${url}/${id}`);
          return resourceReponseDeserializer(response, attributes);
        } catch (error) {
          throw resourceErrorsReponseDeserializer(error, attributes);
        }
      },
      async restoreResource(_, { id }) {
        try {
          const response = await http.put(`${url}/${id}/restore`);
          return resourceReponseDeserializer(response, attributes);
        } catch (error) {
          throw resourceErrorsReponseDeserializer(error, attributes);
        }
      },

      setPagination({ commit }, pagination) {
        commit(SET_RESOURCES_PAGINATION, pagination);
      },
      setSort({ commit }, sort) {
        commit(SET_RESOURCES_SORT, sort);
      },
      setFilters({ commit }, filters) {
        commit(SET_RESOURCES_FILTERS, filters);
      },
    },
  };
};
